import { notificationController } from 'infrastructure/antd/controllers/notification-controller';
import { ModuleType } from 'infrastructure/enums';
import { useGetKeyNote, useSaveKeyNote } from 'infrastructure/hooks/key-notes';
import { useModule } from 'infrastructure/providers/module-provider';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  patientGuid: string;
  canChangeModule?: boolean;
  opened?: boolean;
}
interface ISaveProps {
  note: string;
  expiresAt?: Date | null;
}

export const useKeyNote = ({
  patientGuid,
  canChangeModule,
  opened = false,
}: Props) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [isFirstLoading, setIsFirstLoading] = useState(true);
  const [initialModule, setInitialModule] = useState(ModuleType.RPM);
  const { module: commonModule, changeModule: setCurrentModule } =
    useModule(initialModule);
  const [open, setOpen] = useState<boolean>(false);

  const onOpen = () => setOpen((prev) => !prev);

  const currentModule = useMemo(() => {
    return isFirstLoading ? initialModule : commonModule;
  }, [isFirstLoading, initialModule, commonModule]);

  const [showExpired, setShowExpired] = useState<boolean>(false);

  const { saveNote, isLoading } = useSaveKeyNote({
    module: currentModule,
  });
  const { t } = useTranslation();
  const [
    { data: rpmData, isFetching: rpmLoading, isFetched: rpmFetched },
    { data: ccmData, isFetching: ccmLoading, isFetched: ccmFetched },
  ] = useGetKeyNote({
    module: currentModule,
    patientGuid,
    canChangeModule,
    showExpired,
  });

  const data = currentModule === ModuleType.RPM ? rpmData : ccmData;
  const loading = rpmLoading || ccmLoading;

  const isDataEmpty = useMemo(() => !data?.length, [data]);

  const closeEditMode = () => {
    setIsEditMode(false);
  };

  const handleTabClick = (tabKey: ModuleType) => {
    setCurrentModule(tabKey);
    setInitialModule(tabKey);
    setIsEditMode(false);
  };

  const handleSave = ({ note, expiresAt }: ISaveProps) => {
    saveNote(
      {
        patientGuid,
        module: currentModule,
        note,
        expiresAt: expiresAt || null,
        showExpired,
      },
      {
        onSuccess() {
          closeEditMode();
          notificationController.success({
            message: t('messages.keynoteAdded'),
          });
        },
      },
    );
  };

  const tabs = [
    {
      key: ModuleType.RPM,
      label: t('tabs.rpm'),
    },
    {
      key: ModuleType.CCM,
      label: t('tabs.ccm'),
    },
  ].filter((x) => canChangeModule || x.key === currentModule);

  useEffect(() => {
    if (opened) {
      const hasRpm = rpmFetched && rpmData?.length;
      const hasCcm = ccmFetched && ccmData?.length;

      if (hasCcm && !hasRpm) {
        setInitialModule(ModuleType.CCM);
      }
      if (hasRpm || hasCcm) {
        setOpen(true);
      }
    }
  }, [opened, rpmData?.length, ccmData?.length, rpmFetched, ccmFetched]);

  useEffect(() => {
    if (commonModule !== initialModule) {
      setIsFirstLoading(false);
    }
  }, [commonModule]);

  return {
    currentModule,
    expanded,
    isDataEmpty,
    loading,
    isEditMode,
    tabs,
    isLoading,
    data,
    rpmData,
    ccmData,
    open,
    setOpen,
    showExpired,
    onOpen,
    setShowExpired,
    handleTabClick,
    handleSave,
    closeEditMode,
    setIsEditMode,
    setExpanded,
  };
};
